/**
 * v135 — Share engine type contracts.
 *
 * The share engine generates a 1200×630 PNG (the "OG card") representing one
 * of three things the user might want to share:
 *
 *   - A BUILD: their saved arrow + setup, with key stats and arrow render
 *   - A SESSION: a finished range session, with shot dots + aggregates
 *   - A COMPARE: a multi-source target overlay with per-source legend
 *
 * The shapes below are the minimal data each variant needs to render. They're
 * intentionally normalized — the share card never reaches back into the live
 * BuildState/ShotSession types directly, so the renderer can be unit-tested
 * with fixture data and lives independently of the rest of the codebase.
 *
 * Privacy stance (carried into every variant):
 *   - NO PII by default (no name, no GPS, no per-user labels)
 *   - User can opt to add a custom label or initials
 *   - Photo backgrounds from the photo-overlay flow are NOT included unless
 *     the user explicitly toggles "Include target photo"
 *   - The Sparrow Concierge emblem is always present (free brand impression)
 */

import type { BuildState, BuildDerived } from '@/types';
import type { ShotSession } from '@/range/types';

/** A single shot dot in inch-coordinates from POA. */
export interface ShareShotDot {
  xIn: number;
  yIn: number;
  /** Optional set/source tag for color-keying. */
  sourceKey?: string;
}

/** Aggregate stats shown on session + compare cards. */
export interface ShareAggregates {
  /** Total shot count across all sets / sources in the snapshot. */
  count: number;
  /** Mean radial distance from POA, inches. */
  meanRadiusIn: number;
  /** 95% group radius, inches. Equivalent to 2σ of radial offsets. */
  group95RadiusIn: number;
  /** Primary distance (yd) — null if the snapshot mixes distances. */
  distanceYd: number | null;
}

/** Per-source legend row on compare cards. */
export interface ShareCompareSource {
  /** Display name of the source (set label / session label). */
  label: string;
  /** CSS-compatible color (e.g. "#22d3ee", "rgb(245,158,11)"). */
  color: string;
  /** Per-source stats. */
  count: number;
  meanRadiusIn: number;
}

/**
 * Build-variant snapshot.
 *
 * What the user shares: "Here's my hunting arrow."
 *
 * Drives the build card: arrow diagram (mirrors LivePreview canon) + the five
 * marquee stats + a tagline beneath. Sight tape mini at right is rendered
 * when `sightTapeDistances` is non-empty.
 */
export interface ShareBuildSnapshot {
  variant: 'build';
  /** Build state — read by the SVG composer for shaft / point / vane choices. */
  state: BuildState;
  /** Derived metrics — read for the stats row. */
  derived: BuildDerived;
  /** Optional sight-tape distance ladder for the mini render. */
  sightTapeDistances?: number[];
  /** Optional user-chosen label (initials, "Sal's elk rig", etc.). */
  customLabel?: string;
  /** Optional Sal-voice tagline beneath the stats. */
  tagline?: string;
}

/**
 * Session-variant snapshot.
 *
 * What the user shares: "Here's the group I shot today."
 *
 * Drives the session card: target overlay with shot dots + aggregate stats
 * + session label + conditions row.
 */
export interface ShareSessionSnapshot {
  variant: 'session';
  /** Source session for label + distance + conditions. */
  session: ShotSession;
  /** Flat list of shot dots in POA-relative inches. */
  shots: ShareShotDot[];
  /** Aggregate stats panel. */
  aggregates: ShareAggregates;
  /** Photo data URL (only included when user explicitly opts in). */
  photoDataUri?: string;
  /** Optional user-chosen label (initials, "Sat AM session", etc.). */
  customLabel?: string;
}

/**
 * Compare-variant snapshot.
 *
 * What the user shares: "Here's two of my setups side-by-side at the same
 * range." Drives the compare card: multi-color target overlay with per-source
 * legend rows and per-source stats.
 */
export interface ShareCompareSnapshot {
  variant: 'compare';
  /** Shot dots for every source, color-keyed via `sourceKey` matching the
   *  `sources[].label` for legend lookup. */
  shots: ShareShotDot[];
  /** Per-source rows. Order is preserved in the legend. */
  sources: ShareCompareSource[];
  /** Aggregate stats (computed across all sources). */
  aggregates: ShareAggregates;
  /** Optional user-chosen label. */
  customLabel?: string;
}

/** Union — pass to ShareCardSVG, useShareCard, ShareModal. */
export type ShareSnapshot =
  | ShareBuildSnapshot
  | ShareSessionSnapshot
  | ShareCompareSnapshot;

/** Privacy preferences captured in ShareModal before generation. */
export interface SharePrivacyOptions {
  /** When true, the custom label is rendered on the card. */
  includeLabel: boolean;
  /** When true (session variant only), the user's target photo is rendered
   *  as the card background. Off by default to avoid leaking the user's
   *  backyard / range location. */
  includePhoto: boolean;
}

export const DEFAULT_PRIVACY: SharePrivacyOptions = {
  includeLabel: false,
  includePhoto: false,
};
